"use client";

import { useEffect, useMemo, useState } from "react";
import { CatalogAutocomplete, resolveCatalogEan } from "./catalog-autocomplete";

type Operator = { name: string; pharmacy: string };
type OrderStatus = "new" | "ordered" | "stopped" | "other";
type OrderRequest = {
  id: number;
  pharmacy: string;
  pharmacies: string[];
  sourceIds: number[];
  requestedBy: string;
  requestDate: string;
  product: string;
  ean: string;
  comment: string;
  status: OrderStatus;
  laboratory: string;
  basketItemId: number | null;
  createdAt: number;
  updatedAt: number;
};
type BasketItem = {
  id: number;
  laboratory: string;
  pharmacy: string;
  addedBy: string;
  product: string;
  ean: string;
  quantity: number;
  unit: string;
  note: string;
  francoGoal: number;
  francoUnit: string;
  status: "waiting" | "ordered";
  orderedAt: number | null;
  orderedBy: string;
  sourceRequestId: number | null;
  createdAt: number;
  updatedAt: number;
};

const pharmacies = ["Square", "Ragadan", "Laurentine"] as const;
const mutualizedLaboratories = [
  { name: "Podowell", icon: "👟", detail: "Commandes chaussures mutualisées" },
  { name: "Medi-Comfort", icon: "🦶", detail: "Regroupement du matériel confort" },
  { name: "Donjoy", icon: "🦿", detail: "Orthèses et dispositifs mutualisés" },
  { name: "Sigvaris", icon: "🧦", detail: "Compression médicale mutualisée" },
] as const;
const statusLabels: Record<OrderStatus, string> = {
  new: "Nouveau",
  ordered: "Commandé",
  stopped: "Arrêté",
  other: "Autre",
};

const toCamelRequest = (row: Record<string, unknown>): OrderRequest => ({
  id: Number(row.id),
  pharmacy: String(row.pharmacy ?? ""),
  pharmacies: Array.isArray(row.pharmacies) && row.pharmacies.length
    ? row.pharmacies.map(String).filter((item) => pharmacies.includes(item as (typeof pharmacies)[number]))
    : [String(row.pharmacy ?? "")].filter((item) => pharmacies.includes(item as (typeof pharmacies)[number])),
  sourceIds: [Number(row.id)],
  requestedBy: String(row.requested_by ?? ""),
  requestDate: String(row.request_date ?? ""),
  product: String(row.product ?? ""),
  ean: String(row.ean ?? ""),
  comment: String(row.comment ?? ""),
  status: String(row.status ?? "new") as OrderStatus,
  laboratory: String(row.laboratory ?? ""),
  basketItemId: row.basket_item_id == null ? null : Number(row.basket_item_id),
  createdAt: Number(row.created_at ?? 0),
  updatedAt: Number(row.updated_at ?? 0),
});

function groupCommonRequests(items: OrderRequest[]) {
  const grouped = new Map<string, OrderRequest[]>();
  for (const item of items) {
    const key = [
      item.status,
      item.ean.trim(),
      item.product.trim().toLocaleLowerCase("fr"),
      item.comment.trim().toLocaleLowerCase("fr"),
      item.requestedBy.trim().toLocaleLowerCase("fr"),
      item.requestDate,
      item.laboratory.trim().toLocaleLowerCase("fr"),
    ].join("|");
    const candidates = grouped.get(key) ?? [];
    const current = candidates.find((candidate) => Math.abs(candidate.createdAt - item.createdAt) <= 120_000);
    if (!current) {
      grouped.set(key, [...candidates, { ...item, pharmacies: [...item.pharmacies], sourceIds: [...item.sourceIds] }]);
      continue;
    }
    current.pharmacies = [...new Set([...current.pharmacies, ...item.pharmacies])];
    current.sourceIds = [...new Set([...current.sourceIds, ...item.sourceIds])];
    current.createdAt = Math.min(current.createdAt, item.createdAt);
    current.updatedAt = Math.max(current.updatedAt, item.updatedAt);
  }
  return [...grouped.values()].flat();
}

const toCamelBasket = (row: Record<string, unknown>): BasketItem => ({
  id: Number(row.id),
  laboratory: String(row.laboratory ?? ""),
  pharmacy: String(row.pharmacy ?? ""),
  addedBy: String(row.added_by ?? ""),
  product: String(row.product ?? ""),
  ean: String(row.ean ?? ""),
  quantity: Number(row.quantity ?? 1),
  unit: String(row.unit ?? "u."),
  note: String(row.note ?? ""),
  francoGoal: Number(row.franco_goal ?? 0),
  francoUnit: String(row.franco_unit ?? "unités"),
  status: String(row.status ?? "waiting") as "waiting" | "ordered",
  orderedAt: row.ordered_at == null ? null : Number(row.ordered_at),
  orderedBy: String(row.ordered_by ?? ""),
  sourceRequestId: row.source_request_id == null ? null : Number(row.source_request_id),
  createdAt: Number(row.created_at ?? 0),
  updatedAt: Number(row.updated_at ?? 0),
});

function todayIso() {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function formatDate(value: string) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function parseBulkLines(value: string) {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const ean = line.match(/\b\d{8,14}\b/)?.[0] ?? "";
      const quantityMatch = line.match(/(?:\bx\s*(\d+(?:[.,]\d+)?)\b)|(?:(\d+(?:[.,]\d+)?)\s*x\b)/i);
      const quantity = Number((quantityMatch?.[1] ?? quantityMatch?.[2] ?? "1").replace(",", "."));
      const product = line
        .replace(ean, "")
        .replace(/(?:\bx\s*\d+(?:[.,]\d+)?\b)|(?:\b\d+(?:[.,]\d+)?\s*x\b)/gi, "")
        .replace(/\s{2,}/g, " ")
        .replace(/^[\s\-–—:;]+|[\s\-–—:;]+$/g, "")
        .trim();
      return { product, ean, quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1 };
    })
    .filter((item) => item.product || item.ean);
}

async function enrichBulkLinesWithCatalog(lines: ReturnType<typeof parseBulkLines>) {
  return Promise.all(lines.map(async (line) => {
    if (!line.ean) return line;
    try {
      const suggestion = await resolveCatalogEan(line.ean);
      return { ...line, product: suggestion?.product || line.product || `Produit EAN ${line.ean}` };
    } catch {
      return { ...line, product: line.product || `Produit EAN ${line.ean}` };
    }
  }));
}

function emptyRequestForm(pharmacy: string, operator: string) {
  return { pharmacy, requestedBy: operator, requestDate: todayIso(), product: "", ean: "", comment: "" };
}

type ProductsToOrderModuleProps = {
  onBack: () => void;
  initialPharmacy: string;
  initialOperator: string;
  operators: Operator[];
};

export function ProductsToOrderModule({ onBack, initialPharmacy, initialOperator, operators }: ProductsToOrderModuleProps) {
  const [tab, setTab] = useState<"requests" | "basket">("requests");
  const [entryMode, setEntryMode] = useState<"consult" | "add" | null>(null);
  const [requests, setRequests] = useState<OrderRequest[]>([]);
  const [basketItems, setBasketItems] = useState<BasketItem[]>([]);
  const [syncedAt, setSyncedAt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [requestForm, setRequestForm] = useState(() => emptyRequestForm(initialPharmacy, initialOperator));
  const [catalogLookup, setCatalogLookup] = useState("");
  const [requestPharmacies, setRequestPharmacies] = useState<string[]>(() =>
    pharmacies.includes(initialPharmacy as (typeof pharmacies)[number]) ? [initialPharmacy] : ["Square"],
  );
  const [requestBulk, setRequestBulk] = useState("");
  const [pendingRequestRows, setPendingRequestRows] = useState<Array<{ product: string; ean: string }>>([]);
  const [requestSearch, setRequestSearch] = useState("");
  const [requestPharmacyFilters, setRequestPharmacyFilters] = useState<string[]>([...pharmacies]);
  const [requestStatus, setRequestStatus] = useState<"active" | "history">("active");
  const [requestLabChoices, setRequestLabChoices] = useState<Record<number, string>>({});
  const [laboratoryNames, setLaboratoryNames] = useState<string[]>([]);
  const [historyDays, setHistoryDays] = useState(7);
  const [basketForm, setBasketForm] = useState({
    laboratory: "Podowell",
    pharmacy: initialPharmacy,
    addedBy: initialOperator,
    product: "",
    ean: "",
    quantity: 1,
    unit: "paires",
    note: "",
    francoGoal: 0,
    francoUnit: "paires",
  });
  const [basketBulk, setBasketBulk] = useState("");
  const [basketLabFilter, setBasketLabFilter] = useState<string>("Tous");

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [response, laboratoriesResponse] = await Promise.all([
        fetch("/api/product-orders", { cache: "no-store" }),
        fetch("/api/laboratories", { cache: "no-store" }),
      ]);
      const data = await response.json() as { requests?: Record<string, unknown>[]; basketItems?: Record<string, unknown>[]; historyDays?: number; syncedAt?: number; error?: string };
      if (!response.ok) throw new Error(data.error || "Synchronisation impossible.");
      setRequests((data.requests ?? []).map(toCamelRequest));
      setBasketItems((data.basketItems ?? []).map(toCamelBasket));
      setHistoryDays(Number(data.historyDays ?? 7));
      setSyncedAt(Number(data.syncedAt ?? Date.now()));
      if (laboratoriesResponse.ok) {
        const laboratories = await laboratoriesResponse.json() as { items?: Record<string, unknown>[] };
        setLaboratoryNames((laboratories.items ?? [])
          .filter((item) => Number(item.active ?? 1) !== 0)
          .map((item) => String(item.name ?? "").trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "fr")));
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Synchronisation impossible.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    const initialTimer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(initialTimer);
  }, []);

  const operatorsFor = (pharmacy: string) => operators.filter((operator) => operator.pharmacy === pharmacy);
  const commonRequests = useMemo(() => groupCommonRequests(requests), [requests]);
  const activeRequests = commonRequests.filter((item) => item.status === "new");
  const historyRequests = commonRequests.filter((item) => item.status !== "new");
  const waitingBasket = basketItems.filter((item) => item.status === "waiting");
  const basketGroups = useMemo(() => {
    const groups = new Map<string, BasketItem[]>();
    basketItems.forEach((item) => {
      const key = item.laboratory.trim().toLocaleLowerCase("fr");
      groups.set(key, [...(groups.get(key) ?? []), item]);
    });
    return [...groups.values()]
      .filter((items) => basketLabFilter === "Tous" || items[0].laboratory === basketLabFilter)
      .sort((a, b) => {
        const aWaiting = a.some((item) => item.status === "waiting");
        const bWaiting = b.some((item) => item.status === "waiting");
        return Number(bWaiting) - Number(aWaiting) || a[0].laboratory.localeCompare(b[0].laboratory, "fr");
      });
  }, [basketItems, basketLabFilter]);
  const readyGroups = basketGroups.filter((items) => {
    const waiting = items.filter((item) => item.status === "waiting");
    const goal = Math.max(...waiting.map((item) => item.francoGoal), 0);
    return goal > 0 && waiting.reduce((sum, item) => sum + item.quantity, 0) >= goal;
  }).length;

  const filteredRequests = commonRequests.filter((item) => {
    const query = requestSearch.trim().toLocaleLowerCase("fr");
    const queryMatch = !query || `${item.product} ${item.ean} ${item.comment} ${item.requestedBy}`.toLocaleLowerCase("fr").includes(query);
    const pharmacyMatch = item.pharmacies.some((pharmacy) => requestPharmacyFilters.includes(pharmacy));
    const statusMatch = requestStatus === "active" ? item.status === "new" : item.status !== "new";
    return queryMatch && pharmacyMatch && statusMatch;
  });
  const parsedRequestBulk = useMemo(() => parseBulkLines(requestBulk), [requestBulk]);

  const postRequest = async (payload: Record<string, unknown>) => {
    const response = await fetch("/api/product-orders", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await response.json() as Record<string, unknown> & { error?: string };
    if (!response.ok) throw new Error(data.error || "Enregistrement impossible.");
    return data;
  };

  const prepareSingleRequest = async () => {
    const raw = catalogLookup.trim();
    let productName = requestForm.product.trim();
    let productEan = requestForm.ean.trim() || raw.match(/\d{8,14}/)?.[0] || "";
    if (productEan && !productName) {
      try {
        const suggestion = await resolveCatalogEan(productEan);
        productName = suggestion?.product || "";
        productEan = suggestion?.ean || productEan;
      } catch {
        // La saisie manuelle reste possible si le catalogue est momentanément indisponible.
      }
    }
    if (!productName && raw && !/^\*?\d+$/.test(raw)) productName = raw;
    if (!productName) return setMessage("Choisissez le produit proposé par le catalogue ou ajoutez sa dénomination.");
    setPendingRequestRows([{ product: productName, ean: productEan }]);
  };

  const prepareBulkRequests = async () => {
    const lines = await enrichBulkLinesWithCatalog(parseBulkLines(requestBulk));
    if (!lines.length) return setMessage("Collez au moins une ligne EAN ou EAN + dénomination.");
    setPendingRequestRows(lines.map(({ product, ean }) => ({ product, ean })));
  };

  const savePendingRequests = async () => {
    if (!pendingRequestRows.length || !requestPharmacies.length) return;
    setBusy(true);
    setMessage("");
    try {
      for (const line of pendingRequestRows) {
        await postRequest({
          kind: "request",
          ...requestForm,
          pharmacy: requestPharmacies[0],
          pharmacies: requestPharmacies,
          product: line.product,
          ean: line.ean,
        });
      }
      const createdCount = pendingRequestRows.length;
      setPendingRequestRows([]);
      setCatalogLookup("");
      setRequestBulk("");
      setRequestForm((current) => ({ ...emptyRequestForm(current.pharmacy, current.requestedBy), comment: "" }));
      setMessage(`${createdCount} demande${createdCount > 1 ? "s" : ""} ajoutée${createdCount > 1 ? "s" : ""} à la liste.`);
      await load(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };

  const addSingleRequest = async () => {
    if (!requestPharmacies.length) return setMessage("Choisissez au moins une officine concernée.");
    setBusy(true);
    setMessage("");
    try {
      await postRequest({
        kind: "request",
        ...requestForm,
        pharmacy: requestPharmacies[0],
        pharmacies: requestPharmacies,
      });
      setRequestForm(emptyRequestForm(requestForm.pharmacy, requestForm.requestedBy));
      setCatalogLookup("");
      setMessage(
        requestPharmacies.length > 1
          ? `Demande créée pour ${requestPharmacies.length} officines.`
          : "Demande ajoutée à la liste commune.",
      );
      await load(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Enregistrement impossible.");
    } finally {
      setBusy(false);
    }
  };

  const importRequestBulk = async () => {
    const parsedLines = parseBulkLines(requestBulk);
    const lines = await enrichBulkLinesWithCatalog(parsedLines);
    if (!lines.length) return setMessage("Collez au moins une ligne produit.");
    if (!requestPharmacies.length) return setMessage("Choisissez au moins une officine concernée.");
    setBusy(true);
    setMessage("");
    try {
      for (const line of lines) {
        await postRequest({
          kind: "request",
          ...requestForm,
          pharmacy: requestPharmacies[0],
          pharmacies: requestPharmacies,
          product: line.product,
          ean: line.ean,
        });
      }
      setRequestBulk("");
      const createdCount = lines.length;
      setMessage(
        `${createdCount} demande${createdCount > 1 ? "s" : ""} créée${createdCount > 1 ? "s" : ""} pour ${requestPharmacies.length} officine${requestPharmacies.length > 1 ? "s" : ""}.`,
      );
      await load(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Import impossible.");
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (item: OrderRequest, status: OrderStatus) => {
    const sourceIds = new Set(item.sourceIds);
    setRequests((current) => current.map((entry) => sourceIds.has(entry.id) ? { ...entry, status } : entry));
    try {
      for (const id of item.sourceIds) {
        const response = await fetch("/api/product-orders", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kind: "request", id, action: "set_status", status }),
        });
        if (!response.ok) throw new Error();
      }
    } catch {
      await load(true);
      setMessage("Le statut n’a pas pu être modifié.");
    }
  };

  const sendToStockLabels = async (item: OrderRequest, requestType: "stock" | "label") => {
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch("/api/stock-labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{
            ean: item.ean,
            product: item.product,
            requestType,
            pharmacies: item.pharmacies,
            comment: item.comment,
            createdBy: initialOperator,
          }],
        }),
      });
      const payload = await response.json() as { error?: string };
      if (!response.ok) throw new Error(payload.error || "Transfert impossible.");
      setMessage(
        requestType === "stock"
          ? `${item.product} est envoyé dans « Stock à contrôler ».`
          : `${item.product} est envoyé dans « Étiquette à retirer ».`,
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Transfert impossible.");
    } finally {
      setBusy(false);
    }
  };

  const sendToLaboratoryBasket = async (item: OrderRequest) => {
    const laboratory = requestLabChoices[item.id] ?? "";
    if (!laboratory) return setMessage("Choisissez d’abord le laboratoire concerné.");
    setBusy(true);
    setMessage("");
    try {
      const laboratoryResponse = await fetch("/api/laboratories", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "add_product",
        laboratory,
        pharmacy: item.pharmacy,
        addedBy: initialOperator,
        product: item.product,
        ean: item.ean,
        note: item.comment,
        sourceRequestId: item.id,
        }),
      });
      const laboratoryPayload = await laboratoryResponse.json() as { error?: string };
      if (!laboratoryResponse.ok) throw new Error(laboratoryPayload.error || "Ajout au panier laboratoire impossible.");
      for (const id of item.sourceIds) {
        const linkResponse = await fetch("/api/product-orders", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kind: "request", id, action: "link_laboratory", laboratory }),
        });
        if (!linkResponse.ok) throw new Error("Le produit est dans le panier labo, mais la demande n’a pas pu être reliée.");
      }
      setMessage(`${item.product} est ajouté au panier du laboratoire ${laboratory}.`);
      await load(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ajout au panier impossible.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (kind: "request" | "basket", id: number, sourceIds: number[] = [id]) => {
    if (!window.confirm("Supprimer définitivement cet élément ?")) return;
    const responses = await Promise.all(sourceIds.map((sourceId) => fetch(`/api/product-orders?kind=${kind}&id=${sourceId}`, { method: "DELETE" })));
    if (responses.every((response) => response.ok)) {
      if (kind === "request") {
        const removed = new Set(sourceIds);
        setRequests((current) => current.filter((item) => !removed.has(item.id)));
      } else setBasketItems((current) => current.filter((item) => item.id !== id));
    } else {
      setMessage("La suppression a échoué.");
    }
  };

  const addBasketItem = async (payload: Record<string, unknown>) => {
    setBusy(true);
    setMessage("");
    try {
      await postRequest({ kind: "basket", ...payload });
      setMessage("Produit ajouté au panier mutualisé.");
      await load(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Ajout au panier impossible.");
    } finally {
      setBusy(false);
    }
  };

  const importBasketBulk = async () => {
    const parsedLines = parseBulkLines(basketBulk);
    const lines = await enrichBulkLinesWithCatalog(parsedLines);
    if (!basketForm.laboratory.trim()) return setMessage("Choisissez d’abord un laboratoire mutualisé.");
    if (!lines.length) return setMessage("Collez au moins une ligne produit.");
    setBusy(true);
    try {
      for (const line of lines) {
        await postRequest({
          kind: "basket",
          ...basketForm,
          product: line.product,
          ean: line.ean,
          quantity: line.quantity,
        });
      }
      setBasketBulk("");
      setMessage(`${lines.length} ligne${lines.length > 1 ? "s" : ""} ajoutée${lines.length > 1 ? "s" : ""} au panier.`);
      await load(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Import impossible.");
    } finally {
      setBusy(false);
    }
  };

  const toggleGroup = async (laboratory: string, ordered: boolean) => {
    setBusy(true);
    try {
      const response = await fetch("/api/product-orders", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "basket",
          action: ordered ? "order_group" : "reopen_group",
          laboratory,
          addedBy: initialOperator,
        }),
      });
      if (!response.ok) throw new Error();
      setMessage(ordered ? `Commande ${laboratory} passée.` : `Panier ${laboratory} rouvert.`);
      await load(true);
    } catch {
      setMessage("Le panier n’a pas pu être mis à jour.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="pto-page">
      <header className="pto-topbar">
        <button type="button" className="pto-back" onClick={onBack}>← Accueil</button>
        <a className="pto-tools" href="/?tools=1"><span>🧰</span> Applications</a>
        <div className="pto-sync"><i /> {syncedAt ? `Synchronisé à ${new Date(syncedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : "Connexion…"}</div>
      </header>

      <section className="pto-hero">
        <div>
          <p className="pto-kicker">DEUX BESOINS · DEUX LISTES DISTINCTES</p>
          <h1><span>📦</span> Produits à commander</h1>
          <p>Les demandes de produits servent à remonter un besoin au quotidien. Le panier mutualisé regroupe les commandes Podowell, Medi-Comfort, Donjoy et Sigvaris.</p>
        </div>
        <div className="pto-stats">
          <article><strong>{activeRequests.length}</strong><span>demandes actives</span></article>
          <article><strong>{new Set(waitingBasket.map((item) => item.laboratory.toLocaleLowerCase("fr"))).size}</strong><span>labos en attente</span></article>
          <article className={readyGroups ? "ready" : ""}><strong>{readyGroups}</strong><span>prêts à commander</span></article>
        </div>
      </section>

      <nav className="pto-tabs" aria-label="Vues du module">
        <button type="button" className={tab === "requests" ? "active" : ""} onClick={() => setTab("requests")}>
          <span>📝</span><b>Demande de produit</b><small>Liste des besoins des équipes</small><em>{activeRequests.length}</em>
        </button>
        <button type="button" className={tab === "basket" ? "active" : ""} onClick={() => setTab("basket")}>
          <span>🧺</span><b>Panier mutualisé</b><small>Commandes groupées uniquement</small><em>{waitingBasket.length}</em>
        </button>
      </nav>

      {message && <div className="pto-message" role="status">{message}<button type="button" onClick={() => setMessage("")}>×</button></div>}

      {tab === "requests" ? (
        <>
          {entryMode === null && (
            <div className="pto-entry-choice-overlay" role="dialog" aria-modal="true" aria-labelledby="pto-entry-title">
              <section className="pto-entry-choice">
                <button type="button" className="close" onClick={onBack} aria-label="Fermer">×</button>
                <span>PRODUITS À COMMANDER</span>
                <h2 id="pto-entry-title">Que voulez-vous faire&nbsp;?</h2>
                <p>Choisissez votre chemin : la liste reste claire et le formulaire n’occupe la place que lorsque vous en avez besoin.</p>
                <div>
                  <button type="button" className="consult" onClick={() => setEntryMode("consult")}>
                    <i>📋</i><strong>Consulter la liste</strong><small>Voir les demandes et leur état</small>
                  </button>
                  <button type="button" className="add" onClick={() => setEntryMode("add")}>
                    <i>＋</i><strong>Ajouter des produits</strong><small>Scanner, rechercher ou coller des EAN</small>
                  </button>
                </div>
              </section>
            </div>
          )}
          <div className="pto-request-mode-switch">
            <button type="button" className={entryMode === "consult" ? "active" : ""} onClick={() => setEntryMode("consult")}>📋 Consulter les demandes</button>
            <button type="button" className={entryMode === "add" ? "active" : ""} onClick={() => setEntryMode("add")}>＋ Ajouter des produits</button>
          </div>
          {entryMode === "add" && <section className="pto-panel pto-entry-panel">
            <div className="pto-section-title">
              <div><span>01</span><h2>Ajouter à la liste des demandes</h2><p>Un produit demandé au comptoir, sans aucun lien avec le panier mutualisé.</p></div>
              <button type="button" className="pto-refresh" onClick={() => void load()} disabled={loading}>↻ Actualiser</button>
            </div>
            <div className="pto-catalog-entry">
              <div className="pto-catalog-entry-copy">
                <span>⚡ AJOUT RAPIDE</span>
                <h3>Collez ou scannez simplement l’EAN</h3>
                <p>La dénomination est retrouvée automatiquement. Vous pouvez aussi taper un nom ou <b>*1234</b>.</p>
              </div>
              <label>
                <span>EAN ou dénomination</span>
                <CatalogAutocomplete
                  value={catalogLookup}
                  onChange={setCatalogLookup}
                  onPick={(suggestion) => {
                    setCatalogLookup(suggestion.ean);
                    setRequestForm((current) => ({ ...current, ean: suggestion.ean, product: suggestion.product }));
                  }}
                  placeholder="Scanner, saisir un nom ou *fin EAN"
                  inputMode="search"
                />
              </label>
              <label>
                <span>Produit reconnu</span>
                <input
                  value={requestForm.product}
                  onChange={(event) => setRequestForm((current) => ({ ...current, product: event.target.value }))}
                  placeholder="La dénomination apparaît ici"
                />
              </label>
              <button type="button" disabled={busy || (!catalogLookup.trim() && !requestForm.product.trim()) || requestPharmacies.length === 0} onClick={() => void prepareSingleRequest()}>
                {busy ? "Recherche…" : "Continuer →"}
              </button>
            </div>
            <div className="pto-request-paste">
              <div className="pto-request-paste-copy">
                <span>⚡ COLLAGE RAPIDE</span>
                <h3>Ou collez plusieurs lignes</h3>
                <p>EAN seul ou EAN + dénomination : le portail complète ce qu’il connaît avant confirmation.</p>
              </div>
              <textarea
                value={requestBulk}
                onChange={(event) => setRequestBulk(event.target.value)}
                placeholder={"3400930123456 DOLIPRANE 1000 MG\n3400930654321 PANSEMENT 10 × 10 CM"}
                aria-label="EAN et nom des produits à ajouter aux demandes"
              />
              <div className="pto-request-paste-footer">
                <span>{requestBulk.trim() ? `${parsedRequestBulk.length} demande${parsedRequestBulk.length > 1 ? "s" : ""} commune${parsedRequestBulk.length > 1 ? "s" : ""} prête${parsedRequestBulk.length > 1 ? "s" : ""}` : "Format : EAN + nom du produit"}</span>
                <button type="button" disabled={busy || parsedRequestBulk.length === 0 || requestPharmacies.length === 0} onClick={() => void prepareBulkRequests()}>
                  {busy ? "Lecture…" : "Continuer →"}
                </button>
              </div>
            </div>
            <details className="pto-manual-request">
              <summary>Ajouter une demande avec une précision ou sans EAN</summary>
              <div className="pto-form-grid">
              <label className="wide">Produit / référence
                <input value={requestForm.product} onChange={(event) => setRequestForm((current) => ({ ...current, product: event.target.value }))} placeholder="Ex. pansement demandé plusieurs fois…" />
              </label>
              <label>EAN <small>facultatif</small>
                <CatalogAutocomplete
                  value={requestForm.ean}
                  onChange={(ean) => setRequestForm((current) => ({ ...current, ean }))}
                  onPick={(suggestion) => setRequestForm((current) => ({ ...current, ean: suggestion.ean, product: suggestion.product || current.product }))}
                  placeholder="Scanner ou saisir"
                  inputMode="numeric"
                />
              </label>
              <label className="full">Précision utile <small>facultatif</small>
                <textarea value={requestForm.comment} onChange={(event) => setRequestForm((current) => ({ ...current, comment: event.target.value }))} placeholder="Fréquence de la demande, patient concerné, alternative déjà essayée…" />
              </label>
              </div>
              <div className="pto-entry-actions">
                <button type="button" className="pto-primary" disabled={busy || !requestForm.product.trim() || requestPharmacies.length === 0} onClick={() => void addSingleRequest()}>＋ Ajouter la demande</button>
              </div>
            </details>
          </section>}

          <section className="pto-panel">
            <div className="pto-section-title">
              <div><span>02</span><h2>Demandes communes</h2><p>Les décisions « commandé », « arrêté » et « autre » restent consultables pendant {historyDays} jours.</p></div>
            </div>
            <div className="pto-filters">
              <label className="search"><span>⌕</span><input value={requestSearch} onChange={(event) => setRequestSearch(event.target.value)} placeholder="Rechercher produit, EAN, note…" /></label>
              <fieldset className="pto-display-pharmacies">
                <legend>Pharmacies affichées</legend>
                <button
                  type="button"
                  className={requestPharmacyFilters.length === pharmacies.length ? "active" : ""}
                  onClick={() => setRequestPharmacyFilters(
                    requestPharmacyFilters.length === pharmacies.length ? [] : [...pharmacies],
                  )}
                >
                  <i>{requestPharmacyFilters.length === pharmacies.length ? "✓" : ""}</i>Toutes
                </button>
                {pharmacies.map((pharmacy) => {
                  const selected = requestPharmacyFilters.includes(pharmacy);
                  return (
                    <button
                      type="button"
                      key={pharmacy}
                      className={selected ? "active" : ""}
                      onClick={() => setRequestPharmacyFilters((current) =>
                        selected ? current.filter((item) => item !== pharmacy) : [...current, pharmacy],
                      )}
                    >
                      <i>{selected ? "✓" : ""}</i>{pharmacy}
                    </button>
                  );
                })}
              </fieldset>
              <select value={requestStatus} onChange={(event) => setRequestStatus(event.target.value as "active" | "history")}>
                <option value="active">À traiter · {activeRequests.length}</option>
                <option value="history">Historique {historyDays} jours · {historyRequests.length}</option>
              </select>
            </div>
            <div className="pto-request-list">
              {loading ? <div className="pto-empty">Chargement des demandes…</div> : filteredRequests.length === 0 ? (
                <div className="pto-empty"><span>✓</span><b>Aucune demande dans cette vue</b><p>Les nouvelles demandes apparaîtront ici en temps réel.</p></div>
              ) : filteredRequests.map((item) => (
                <article className={`pto-request-card status-${item.status}`} key={item.id}>
                  <div className="pto-request-main">
                    <div className="pto-card-heading">
                      {item.pharmacies.map((pharmacy) => <span key={pharmacy} className={`pto-pharmacy pto-${pharmacy.toLocaleLowerCase("fr")}`}>{pharmacy}</span>)}
                      <span className={`pto-status pto-status-${item.status}`}>{statusLabels[item.status]}</span>
                    </div>
                    <h3>{item.product}</h3>
                    <p className="pto-meta">{item.ean ? `EAN ${item.ean} · ` : ""}par <b>{item.requestedBy}</b> · {formatDate(item.requestDate)}</p>
                    {item.comment && <p className="pto-comment">{item.comment}</p>}
                  </div>
                  <div className="pto-card-actions">
                    <div className="pto-status-actions">
                      {(Object.keys(statusLabels) as OrderStatus[]).map((status) => (
                        <button type="button" key={status} className={item.status === status ? "active" : ""} onClick={() => void updateStatus(item, status)}>{statusLabels[status]}</button>
                      ))}
                    </div>
                    <div className="pto-routing-actions">
                      <button type="button" className="stock" disabled={busy} onClick={() => void sendToStockLabels(item, "stock")}>📦 Stock à contrôler</button>
                      <button type="button" className="label" disabled={busy} onClick={() => void sendToStockLabels(item, "label")}>🏷️ Étiquette à virer</button>
                      <div className="pto-lab-route">
                        <select
                          aria-label={`Laboratoire destinataire pour ${item.product}`}
                          value={requestLabChoices[item.id] ?? ""}
                          disabled={busy || Boolean(item.laboratory)}
                          onChange={(event) => setRequestLabChoices((current) => ({
                            ...current,
                            [item.id]: event.target.value,
                          }))}
                        >
                          <option value="">Choisir le labo</option>
                          {laboratoryNames.map((laboratory) => <option key={laboratory}>{laboratory}</option>)}
                        </select>
                        <button
                          type="button"
                          className="lab-basket"
                          disabled={busy || Boolean(item.laboratory) || !(requestLabChoices[item.id] ?? "")}
                          onClick={() => void sendToLaboratoryBasket(item)}
                        >
                          {item.laboratory ? `✓ Panier ${item.laboratory}` : "🧺 Panier du labo"}
                        </button>
                      </div>
                    </div>
                    <button type="button" className="pto-delete" onClick={() => void remove("request", item.id, item.sourceIds)} title="Supprimer">×</button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      ) : (
        <>
          <section className="pto-panel pto-basket-entry">
            <div className="pto-section-title">
              <div><span>01</span><h2>Panier commun aux trois pharmacies</h2><p>Podowell, Medi-Comfort, Donjoy et Sigvaris. Les quantités s’additionnent automatiquement.</p></div>
            </div>
            <div className="pto-lab-choices" aria-label="Choisir le panier">
              {mutualizedLaboratories.map((laboratory) => (
                <button
                  type="button"
                  key={laboratory.name}
                  className={basketForm.laboratory === laboratory.name ? "active" : ""}
                  onClick={() => setBasketForm((current) => ({ ...current, laboratory: laboratory.name, unit: "paires", francoUnit: "paires" }))}
                >
                  <span>{laboratory.icon}</span>
                  <b>{laboratory.name}</b>
                  <small>{laboratory.detail}</small>
                  <i>{basketForm.laboratory === laboratory.name ? "✓ Panier sélectionné" : "Choisir"}</i>
                </button>
              ))}
            </div>
            <div className="pto-form-grid basket">
              <label>Laboratoire
                <select value={basketForm.laboratory} onChange={(event) => setBasketForm((current) => ({ ...current, laboratory: event.target.value }))}>
                  {mutualizedLaboratories.map((laboratory) => <option key={laboratory.name}>{laboratory.name}</option>)}
                </select>
              </label>
              <label>Officine
                <select value={basketForm.pharmacy} onChange={(event) => {
                  const pharmacy = event.target.value;
                  setBasketForm((current) => ({ ...current, pharmacy, addedBy: operatorsFor(pharmacy)[0]?.name ?? initialOperator }));
                }}>{pharmacies.map((item) => <option key={item}>{item}</option>)}</select>
              </label>
              <label>Ajouté par
                <select value={basketForm.addedBy} onChange={(event) => setBasketForm((current) => ({ ...current, addedBy: event.target.value }))}>
                  {operatorsFor(basketForm.pharmacy).map((operator) => <option key={operator.name}>{operator.name}</option>)}
                </select>
              </label>
              <label className="wide">Produit
                <input value={basketForm.product} onChange={(event) => setBasketForm((current) => ({ ...current, product: event.target.value }))} placeholder="Produit, modèle, taille…" />
              </label>
              <label>Quantité
                <div className="pto-inline-fields"><input type="number" min="0.1" step="0.1" value={basketForm.quantity} onChange={(event) => setBasketForm((current) => ({ ...current, quantity: Number(event.target.value) }))} /><input value={basketForm.unit} onChange={(event) => setBasketForm((current) => ({ ...current, unit: event.target.value }))} /></div>
              </label>
              <label>Objectif franco
                <div className="pto-inline-fields"><input type="number" min="0" step="0.1" value={basketForm.francoGoal || ""} onChange={(event) => setBasketForm((current) => ({ ...current, francoGoal: Number(event.target.value) }))} placeholder="Ex. 12" /><input value={basketForm.francoUnit} onChange={(event) => setBasketForm((current) => ({ ...current, francoUnit: event.target.value }))} /></div>
              </label>
              <label className="full">Note
                <input value={basketForm.note} onChange={(event) => setBasketForm((current) => ({ ...current, note: event.target.value }))} placeholder="Taille, coloris, patient, référence…" />
              </label>
            </div>
            <div className="pto-entry-actions">
              <button type="button" className="pto-primary" disabled={busy || !basketForm.laboratory.trim() || !basketForm.product.trim()} onClick={() => void addBasketItem(basketForm)}>＋ Mettre en attente</button>
              <details className="pto-bulk">
                <summary>⚡ Collage rapide EAN + dénomination</summary>
                <p>Le laboratoire, l’officine et le franco saisis ci-dessus sont appliqués à chaque ligne.</p>
                <textarea value={basketBulk} onChange={(event) => setBasketBulk(event.target.value)} placeholder={"3400000000000 Produit exemple x2\nProduit exemple 3400000000000"} />
                <button type="button" disabled={busy} onClick={() => void importBasketBulk()}>Importer dans le panier</button>
              </details>
            </div>
          </section>

          <section className="pto-panel">
            <div className="pto-section-title">
              <div><span>02</span><h2>Les quatre paniers mutualisés</h2><p>Chaque panier passe en vert dès que le franco commun est atteint.</p></div>
            </div>
            <div className="pto-basket-filters">
              {["Tous", ...mutualizedLaboratories.map((item) => item.name)].map((laboratory) => (
                <button type="button" key={laboratory} className={basketLabFilter === laboratory ? "active" : ""} onClick={() => setBasketLabFilter(laboratory)}>
                  {laboratory === "Tous" ? "Tous les paniers" : laboratory}
                </button>
              ))}
            </div>
            <div className="pto-basket-groups">
              {basketGroups.length === 0 ? <div className="pto-empty"><span>🧺</span><b>Aucun panier mutualisé</b><p>Ajoutez une première ligne pour commencer à regrouper les besoins.</p></div> : basketGroups.map((items) => {
                const laboratory = items[0].laboratory;
                const waiting = items.filter((item) => item.status === "waiting");
                const ordered = waiting.length === 0;
                const total = (ordered ? items : waiting).reduce((sum, item) => sum + item.quantity, 0);
                const goal = Math.max(...items.map((item) => item.francoGoal), 0);
                const unit = items.find((item) => item.francoGoal > 0)?.francoUnit || items[0].francoUnit;
                const ready = !ordered && goal > 0 && total >= goal;
                const progress = goal > 0 ? Math.min(100, Math.round(total / goal * 100)) : 0;
                const pharmacyTotals = pharmacies.map((pharmacy) => ({
                  pharmacy,
                  total: (ordered ? items : waiting).filter((item) => item.pharmacy === pharmacy).reduce((sum, item) => sum + item.quantity, 0),
                })).filter((entry) => entry.total > 0);
                return (
                  <article className={`pto-basket-group ${ready ? "ready" : ""} ${ordered ? "ordered" : ""}`} key={laboratory.toLocaleLowerCase("fr")}>
                    <header>
                      <div><span className="pto-lab-icon">L</span><h3>{laboratory}</h3><em>{ordered ? "Commande passée" : ready ? "Franco atteint" : goal ? `Encore ${Math.max(0, goal - total)} ${unit}` : "Franco à définir"}</em></div>
                      <div className="pto-group-actions">
                        <button type="button" className={ready ? "pto-ready-button" : ""} onClick={() => void toggleGroup(laboratory, !ordered)} disabled={busy}>{ordered ? "↻ Rouvrir" : "✓ Commande passée"}</button>
                      </div>
                    </header>
                    <div className="pto-progress">
                      <div className="pto-progress-label"><b>{total} {unit}</b><span>{goal ? `objectif ${goal} ${unit}` : "objectif non renseigné"}</span></div>
                      <div className="pto-progress-track"><i style={{ width: `${progress}%` }} /></div>
                      <div className="pto-pharmacy-totals">{pharmacyTotals.map((entry) => <span key={entry.pharmacy}><b>{entry.pharmacy}</b>{entry.total} {unit}</span>)}</div>
                    </div>
                    <div className="pto-basket-lines">
                      {items.map((item) => (
                        <div key={item.id}>
                          <span className={`pto-pharmacy pto-${item.pharmacy.toLocaleLowerCase("fr")}`}>{item.pharmacy}</span>
                          <p><b>{item.product}</b><small>{item.ean ? `EAN ${item.ean} · ` : ""}{item.note || `ajouté par ${item.addedBy}`}</small></p>
                          <strong>{item.quantity} {item.unit}</strong>
                          <button type="button" onClick={() => void remove("basket", item.id)} title="Supprimer la ligne">×</button>
                        </div>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </>
      )}

      {pendingRequestRows.length > 0 && (
        <div className="pto-confirm-layer" role="presentation" onMouseDown={() => setPendingRequestRows([])}>
          <section role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div><span>CONFIRMER {pendingRequestRows.length} PRODUIT{pendingRequestRows.length > 1 ? "S" : ""}</span><h2>À qui confier la demande ?</h2></div>
              <button type="button" onClick={() => setPendingRequestRows([])}>×</button>
            </header>
            <div className="pto-confirm-body">
              <fieldset>
                <legend>Pharmacies concernées</legend>
                {pharmacies.map((name) => <button type="button" className={requestPharmacies.includes(name) ? "active" : ""} key={name} onClick={() => setRequestPharmacies((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name])}>{requestPharmacies.includes(name) ? "✓ " : ""}{name}</button>)}
              </fieldset>
              <div className="pto-confirm-grid">
                <label><span>Demandé par</span><select value={requestForm.requestedBy} onChange={(event) => setRequestForm((current) => ({ ...current, requestedBy: event.target.value }))}>{operatorsFor(initialPharmacy).map((operator) => <option key={operator.name}>{operator.name}</option>)}</select></label>
                <label><span>Date</span><input type="date" value={requestForm.requestDate} onChange={(event) => setRequestForm((current) => ({ ...current, requestDate: event.target.value }))} /></label>
              </div>
              <label><span>Commentaire facultatif</span><textarea value={requestForm.comment} onChange={(event) => setRequestForm((current) => ({ ...current, comment: event.target.value.slice(0, 700) }))} placeholder="Fréquence de la demande, patient concerné, détail utile…" /></label>
              <div className="pto-confirm-preview">{pendingRequestRows.slice(0, 6).map((row) => <p key={`${row.ean}-${row.product}`}><b>{row.ean || "Sans EAN"}</b><span>{row.product}</span></p>)}{pendingRequestRows.length > 6 && <p>+ {pendingRequestRows.length - 6} autres produits</p>}</div>
            </div>
            <footer><button type="button" onClick={() => setPendingRequestRows([])}>Annuler</button><button type="button" className="primary" disabled={busy || !requestPharmacies.length} onClick={() => void savePendingRequests()}>{busy ? "Ajout…" : `Ajouter ${pendingRequestRows.length * requestPharmacies.length} demande${pendingRequestRows.length * requestPharmacies.length > 1 ? "s" : ""}`}</button></footer>
          </section>
        </div>
      )}

    </main>
  );
}
